import * as THREE from 'three';
import type { Brush, BrushFaceMaterial } from '../physics/Brush';
import type { EnemySpawn } from '../enemies/EnemySystem';
import {
  applyWorldWallUvs,
  cloneMatWorldUv,
  crateStyleForId,
  createArenaMaterials,
  pickFacilityMaterial,
  uvKindForSurf,
  uvRepeatForBrush,
  uvRepeatForVent,
  type ArenaMats,
} from './ArenaMaterials';
import { segmentedBox, type BakeLight } from './BakeLights';
import type { DoorDesc } from './DoorSystem';
import { dressFacility, type FacilityRoom } from './FacilityDressing';
import type { PickupKind, PickupSpawnDef } from './PickupSystem';

const CRATE_LOOT: Partial<Record<string, PickupKind>> = {
  crate_tank1: 'shells',
  crate_stash_s1: 'pistol_clip',
  crate_stash_w2: 'mp5_clip',
};

/** Start WC: x −5..5, z 0..11, ground floor. */
export function isInBathroom(p: { x: number; y: number; z: number }): boolean {
  return p.x > -4.9 && p.x < 4.9 && p.z > 0.12 && p.z < 10.95 && p.y > -0.25 && p.y < 2.7;
}

/** Crouched duct over the bathroom. */
export function isInVent(p: { x: number; y: number; z: number }): boolean {
  if (p.y < 2.85 || p.y > 4.6) return false;
  const inStart = p.x > -0.85 && p.x < 0.85 && p.z > 4.05 && p.z < 7.3;
  const inElbow = p.x > -4.6 && p.x < 4.6 && p.z > 4.05 && p.z < 5.5;
  const inEast = p.x > 3.05 && p.x < 4.6 && p.z > 1.55 && p.z < 5.5;
  const inWest = p.x > -4.6 && p.x < -3.05 && p.z > 0.95 && p.z < 5.5;
  return inStart || inElbow || inEast || inWest;
}

/** Facility-style corridor complex — same consumer shape as TestArena + spawns. */
export interface FacilityArenaBuild {
  group: THREE.Group;
  brushes: Brush[];
  lights: THREE.Light[];
  spawn: THREE.Vector3;
  spawnYaw: number;
  spawnCrouch?: boolean;
  doors: DoorDesc[];
  mats: ArenaMats;
  enemySpawns: EnemySpawn[];
  pickups: PickupSpawnDef[];
  rooms: FacilityRoom[];
  bakeLights: BakeLight[];
}

const MAT: Record<string, BrushFaceMaterial> = {
  floor: { color: 0x3a3c38, roughness: 0.85, metalness: 0.25 },
  wall: { color: 0x5a6058, roughness: 0.9, metalness: 0.05 },
  accent: { color: 0x4a5040, roughness: 0.7, metalness: 0.35 },
  metal: { color: 0x4a5058, roughness: 0.4, metalness: 0.8 },
  desk: { color: 0x3a3228, roughness: 0.85, metalness: 0.05 },
};

const WALL_H = 3.05;
const WALL_T = 0.4;
const DOOR_W = 1.15;
const DOOR_H = 2.25;
const DOOR_T = 0.08;
const Y0 = 0;
const YB = -3.5;
const YU = 3.6;
const H_BOTTLE = 7.2;
const H_SHAFT_DN = WALL_H - YB;
/** Stairwell ceiling — clears a standing player on the mid landing, not a 6.7 m void. */
const H_STAIRUP = 4.65;
/** Ground-floor annex under the labs — sits below the lab slab (3.2–3.6). */
const H_ANNEX = 3.15;
/** Kitchen / generator party wall, inset off z=35 so it does not z-fight the stair door wall. */
const ANNEX_DOOR_Z = 35.42;
const OPEN_W = 2.5;
/** West face of the glass bays — east of the stair top so you can walk off. */
const LAB_GLASS_X = 25;
const LAB_X1 = 32;
const LAB_BAY_Z = [35, 42, 49, 56] as const;
const LAB_BAY_DOOR_Z = [36.15, 47.55, 50.35] as const;
const LAB_BAY_CX = (LAB_GLASS_X + LAB_X1) * 0.5;
const LAB_BAY_SX = LAB_X1 - LAB_GLASS_X;

type Surf = keyof ArenaMats;

function box(
  id: string,
  x: number,
  y: number,
  z: number,
  sx: number,
  sy: number,
  sz: number,
  material = MAT.wall,
  yaw = 0,
): Brush {
  return {
    id,
    kind: 'box',
    center: new THREE.Vector3(x, y, z),
    size: new THREE.Vector3(sx, sy, sz),
    yaw,
    material,
  };
}

function ramp(
  id: string,
  x: number,
  y: number,
  z: number,
  sx: number,
  sy: number,
  sz: number,
  yaw = 0,
): Brush {
  return {
    id,
    kind: 'ramp',
    center: new THREE.Vector3(x, y, z),
    size: new THREE.Vector3(sx, sy, sz),
    yaw,
    material: MAT.metal,
  };
}

function surfForBrush(id: string): Surf {
  if (id.startsWith('floor_bottle')) return 'paving';
  if (id.startsWith('floor_tanks')) return 'ground';
  if (
    id.startsWith('catwalk') ||
    id.startsWith('lip_cw') ||
    id.startsWith('lip_stair') ||
    id.startsWith('landing')
  ) {
    return 'ramp';
  }
  if (id.startsWith('floor') || id.startsWith('stoop') || id.startsWith('lip')) {
    return 'floor';
  }
  if (id.startsWith('ceil') || id.startsWith('beam')) return 'ceiling';
  if (id.startsWith('ramp') || id.startsWith('step')) return 'ramp';
  if (
    id.startsWith('jamb') ||
    id.startsWith('tank') ||
    id.startsWith('pillar') ||
    id.startsWith('rail')
  ) {
    return 'metal';
  }
  if (id.startsWith('vent_') || id.startsWith('wall_vent')) return 'vent';
  if (id.startsWith('crate') || id.startsWith('sandbag') || id.startsWith('cabinet')) return 'crate';
  if (id.startsWith('stall')) return 'metal';
  if (id.startsWith('wall_bath') && !id.includes('wall_bath_n')) return 'wallBath';
  if (id.startsWith('desk')) return 'desk';
  if (id.startsWith('hazard')) return 'hazard';
  return 'wall';
}

function wallCy(floorY: number, height: number): number {
  return floorY + height * 0.5;
}

function addRoomShell(
  brushes: Brush[],
  rooms: FacilityRoom[],
  spec: FacilityRoom,
  opts: { ceiling?: boolean; skipWall?: Partial<Record<'n' | 's' | 'e' | 'w', boolean>> } = {},
): void {
  rooms.push(spec);
  const { id, x0, z0, x1, z1, floorY, height } = spec;
  const cx = (x0 + x1) * 0.5;
  const cz = (z0 + z1) * 0.5;
  const sx = Math.abs(x1 - x0);
  const sz = Math.abs(z1 - z0);
  const cy = wallCy(floorY, height);
  brushes.push(box(`floor_${id}`, cx, floorY - 0.2, cz, sx, 0.4, sz, MAT.floor));
  if (opts.ceiling !== false) {
    brushes.push(box(`ceil_${id}`, cx, floorY + height + 0.1, cz, sx, 0.25, sz, MAT.metal));
  }
  const skip = opts.skipWall ?? {};
  if (!skip.s) brushes.push(box(`wall_${id}_s`, cx, cy, z0, sx, height, WALL_T, MAT.wall));
  if (!skip.n) brushes.push(box(`wall_${id}_n`, cx, cy, z1, sx, height, WALL_T, MAT.wall));
  if (!skip.w) brushes.push(box(`wall_${id}_w`, x0, cy, cz, WALL_T, height, sz, MAT.wall));
  if (!skip.e) brushes.push(box(`wall_${id}_e`, x1, cy, cz, WALL_T, height, sz, MAT.wall));
}

function addWallWithDoorX(
  brushes: Brush[],
  idPrefix: string,
  wallZ: number,
  xMin: number,
  xMax: number,
  doorCenterX: number,
  floorY = 0,
  height = WALL_H,
  doorW = DOOR_W,
  openH = DOOR_H,
): void {
  const jambT = 0.07;
  const jambD = WALL_T + 0.06;
  const doorL = doorCenterX - doorW * 0.5;
  const doorR = doorCenterX + doorW * 0.5;
  const cutL = doorL - jambT;
  const cutR = doorR + jambT;
  const cy = wallCy(floorY, height);
  const leftW = cutL - xMin;
  if (leftW > 0.08) {
    brushes.push(box(`${idPrefix}_l`, (xMin + cutL) * 0.5, cy, wallZ, leftW, height, WALL_T, MAT.wall));
  }
  const rightW = xMax - cutR;
  if (rightW > 0.08) {
    brushes.push(box(`${idPrefix}_r`, (cutR + xMax) * 0.5, cy, wallZ, rightW, height, WALL_T, MAT.wall));
  }
  const lintelH = height - openH - jambT;
  if (lintelH > 0.05) {
    brushes.push(
      box(
        `${idPrefix}_lintel`,
        doorCenterX,
        floorY + openH + jambT + lintelH * 0.5,
        wallZ,
        doorW + jambT * 2,
        lintelH,
        WALL_T,
        MAT.wall,
      ),
    );
  }
  const jy = floorY + openH * 0.5;
  brushes.push(box(`jamb_${idPrefix}_l`, doorL - jambT * 0.5, jy, wallZ, jambT, openH, jambD, MAT.metal));
  brushes.push(box(`jamb_${idPrefix}_r`, doorR + jambT * 0.5, jy, wallZ, jambT, openH, jambD, MAT.metal));
  brushes.push(
    box(`jamb_${idPrefix}_top`, doorCenterX, floorY + openH + jambT * 0.5, wallZ, doorW + jambT * 2, jambT, jambD, MAT.metal),
  );
}

function addWallWithDoorZ(
  brushes: Brush[],
  idPrefix: string,
  wallX: number,
  zMin: number,
  zMax: number,
  doorCenterZ: number,
  floorY = 0,
  height = WALL_H,
  doorW = DOOR_W,
  openH = DOOR_H,
  /** Solid under a raised opening (stair shaft → foyer). */
  sillH = 0,
): void {
  const jambT = 0.07;
  const jambD = WALL_T + 0.06;
  const doorA = doorCenterZ - doorW * 0.5;
  const doorB = doorCenterZ + doorW * 0.5;
  const cutA = doorA - jambT;
  const cutB = doorB + jambT;
  const cutW = doorW + jambT * 2;
  const cy = wallCy(floorY, height);
  const nearW = cutA - zMin;
  if (nearW > 0.08) {
    brushes.push(box(`${idPrefix}_a`, wallX, cy, (zMin + cutA) * 0.5, WALL_T, height, nearW, MAT.wall));
  }
  const farW = zMax - cutB;
  if (farW > 0.08) {
    brushes.push(box(`${idPrefix}_b`, wallX, cy, (cutB + zMax) * 0.5, WALL_T, height, farW, MAT.wall));
  }
  if (sillH > 0.05) {
    brushes.push(box(`${idPrefix}_sill`, wallX, floorY + sillH * 0.5, doorCenterZ, WALL_T, sillH, cutW, MAT.wall));
  }
  const openBot = floorY + sillH;
  const lintelH = height - sillH - openH - jambT;
  if (lintelH > 0.05) {
    brushes.push(
      box(
        `${idPrefix}_lintel`,
        wallX,
        openBot + openH + jambT + lintelH * 0.5,
        doorCenterZ,
        WALL_T,
        lintelH,
        cutW,
        MAT.wall,
      ),
    );
  }
  const jy = openBot + openH * 0.5;
  brushes.push(box(`jamb_${idPrefix}_a`, wallX, jy, doorA - jambT * 0.5, jambD, openH, jambT, MAT.metal));
  brushes.push(box(`jamb_${idPrefix}_b`, wallX, jy, doorB + jambT * 0.5, jambD, openH, jambT, MAT.metal));
  brushes.push(
    box(`jamb_${idPrefix}_top`, wallX, openBot + openH + jambT * 0.5, doorCenterZ, jambD, jambT, cutW, MAT.metal),
  );
}

function plugWidthGap(
  brushes: Brush[],
  id: string,
  wallZ: number,
  innerMin: number,
  innerMax: number,
  outerMin: number,
  outerMax: number,
  floorY = 0,
  height = WALL_H,
): void {
  const cy = wallCy(floorY, height);
  const leftW = innerMin - outerMin;
  if (leftW > 0.08) {
    brushes.push(box(`${id}_l`, (outerMin + innerMin) * 0.5, cy, wallZ, leftW, height, WALL_T, MAT.wall));
  }
  const rightW = outerMax - innerMax;
  if (rightW > 0.08) {
    brushes.push(box(`${id}_r`, (innerMax + outerMax) * 0.5, cy, wallZ, rightW, height, WALL_T, MAT.wall));
  }
}

function stallDoor(id: string, bayX: number): DoorDesc {
  const w = 0.92;
  const h = 1.52;
  const gap = 0.22;
  return {
    id,
    hinge: new THREE.Vector3(bayX - w * 0.5, gap + h * 0.5, 3.88),
    closedYaw: 0,
    openSign: -1,
    width: w,
    height: h,
    thickness: 0.04,
    style: 'stall',
  };
}

function doorOnXWall(id: string, doorCenterX: number, wallZ: number, openSign: number, floorY = 0): DoorDesc {
  return {
    id,
    hinge: new THREE.Vector3(doorCenterX - DOOR_W * 0.5, floorY + DOOR_H * 0.5, wallZ),
    closedYaw: 0,
    openSign,
    width: DOOR_W,
    height: DOOR_H,
    thickness: DOOR_T,
  };
}

/** Door in a wall at constant X. Leaf closed along +Z; +openSign swings toward +X. */
function doorOnZWall(id: string, wallX: number, doorCenterZ: number, openSign: number, floorY = 0): DoorDesc {
  return {
    id,
    hinge: new THREE.Vector3(wallX, floorY + DOOR_H * 0.5, doorCenterZ - DOOR_W * 0.5),
    closedYaw: -Math.PI * 0.5,
    openSign,
    width: DOOR_W,
    height: DOOR_H,
    thickness: DOOR_T,
  };
}

function addFloorSlab(
  brushes: Brush[],
  id: string,
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  floorY: number,
): void {
  const cx = (x0 + x1) * 0.5;
  const cz = (z0 + z1) * 0.5;
  brushes.push(box(id, cx, floorY - 0.2, cz, Math.abs(x1 - x0), 0.4, Math.abs(z1 - z0), MAT.floor));
}

const RAIL_POST_H = 0.95;
const RAIL_POST_T = 0.055;
const RAIL_BAR_T = 0.05;
const _railGeo = new THREE.BoxGeometry(1, 1, 1);
const _railDir = new THREE.Vector3();
const _railAxis = new THREE.Vector3(1, 0, 0);

/** Horizontal landing / lip rail: posts plus top and mid bars. */
function addGuardRail(
  brushes: Brush[],
  id: string,
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  floorY: number,
): void {
  const dx = x1 - x0;
  const dz = z1 - z0;
  const len = Math.hypot(dx, dz);
  if (len < 0.12) return;
  const nx = dx / len;
  const nz = dz / len;
  const n = Math.max(1, Math.round(len / 0.68));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const thick = i === 0 || i === n ? 0.07 : RAIL_POST_T;
    brushes.push(
      box(
        `rail_${id}_p${i}`,
        x0 + dx * t,
        floorY + RAIL_POST_H * 0.5,
        z0 + dz * t,
        thick,
        RAIL_POST_H,
        thick,
        MAT.metal,
      ),
    );
  }
  const yaw = Math.atan2(-nz, nx);
  const mx = (x0 + x1) * 0.5;
  const mz = (z0 + z1) * 0.5;
  brushes.push(box(`rail_${id}_top`, mx, floorY + RAIL_POST_H, mz, len + 0.06, RAIL_BAR_T, RAIL_BAR_T, MAT.metal, yaw));
  brushes.push(
    box(`rail_${id}_mid`, mx, floorY + RAIL_POST_H * 0.48, mz, len + 0.06, RAIL_BAR_T * 0.85, RAIL_BAR_T * 0.85, MAT.metal, yaw),
  );
}

function addSlopedRail(
  parent: THREE.Object3D,
  mats: ArenaMats,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  thick = RAIL_BAR_T,
): void {
  const dx = bx - ax;
  const dy = by - ay;
  const dz = bz - az;
  const len = Math.hypot(dx, dy, dz);
  if (len < 0.08) return;
  const mesh = new THREE.Mesh(_railGeo, mats.metal);
  mesh.position.set((ax + bx) * 0.5, (ay + by) * 0.5, (az + bz) * 0.5);
  mesh.scale.set(len, thick, thick);
  _railDir.set(dx, dy, dz).normalize();
  mesh.quaternion.setFromUnitVectors(_railAxis, _railDir);
  mesh.castShadow = true;
  parent.add(mesh);
}

/** High end at (hx, highY, hz). Slope rises along local +X after yaw. */
function addStairFlight(
  brushes: Brush[],
  parent: THREE.Object3D,
  mats: ArenaMats,
  id: string,
  hx: number,
  hz: number,
  yaw: number,
  length: number,
  rise: number,
  width: number,
  highY: number,
  sides: { a?: boolean; b?: boolean } = { a: true, b: true },
  /** Skip this fraction of the flight from the high end (opens a landing). */
  trimHigh: { a?: number; b?: number } = {},
): void {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  const cx = hx - c * (length * 0.5);
  const cz = hz + s * (length * 0.5);
  const cy = highY - rise * 0.5;
  brushes.push(ramp(`ramp_${id}`, cx, cy, cz, length, rise, width, yaw));

  const n = Math.max(5, Math.round(length / 0.34));
  for (let i = 0; i < n; i++) {
    const t = (i + 0.55) / n;
    const lx = -length * 0.5 + t * length;
    const ly = -rise * 0.5 + t * rise + 0.035;
    const wx = cx + c * lx;
    const wz = cz - s * lx;
    const step = box(`step_${id}_${i}`, wx, cy + ly, wz, 0.3, 0.055, width - 0.14, MAT.metal, yaw);
    step.noCollision = true;
    brushes.push(step);
  }

  const side = width * 0.5 - 0.06;
  const posts = Math.max(3, Math.round(length / 0.62));
  for (const sign of [-1, 1] as const) {
    const tag = sign < 0 ? 'a' : 'b';
    if (tag === 'a' && sides.a === false) continue;
    if (tag === 'b' && sides.b === false) continue;
    const lz = sign * side;
    const hxSide = hx + s * lz;
    const hzSide = hz + c * lz;
    const lxSide = hx - c * length + s * lz;
    const lzSide = hz + s * length + c * lz;
    const trim = tag === 'a' ? (trimHigh.a ?? 0) : (trimHigh.b ?? 0);
    const t0 = Math.min(0.85, Math.max(0, trim));
    const hxTrim = hxSide + (lxSide - hxSide) * t0;
    const hzTrim = hzSide + (lzSide - hzSide) * t0;
    const hyTrim = highY - t0 * rise;
    for (let i = 0; i <= posts; i++) {
      const t = i / posts;
      if (t < t0 - 1e-6) continue;
      const px = hxSide + (lxSide - hxSide) * t;
      const pz = hzSide + (lzSide - hzSide) * t;
      const surfaceY = highY - t * rise;
      const thick = t <= t0 + 1e-6 || i === posts ? 0.07 : RAIL_POST_T;
      brushes.push(box(`rail_${id}_${tag}_p${i}`, px, surfaceY + RAIL_POST_H * 0.5, pz, thick, RAIL_POST_H, thick, MAT.metal));
      if (i === posts) continue;
      const t1 = (i + 1) / posts;
      const bx = hxSide + (lxSide - hxSide) * t1;
      const bz = hzSide + (lzSide - hzSide) * t1;
      const y1 = highY - t1 * rise;
      const seg = Math.hypot(bx - px, bz - pz);
      const span = box(
        `rail_${id}_${tag}_span${i}`,
        (px + bx) * 0.5,
        (surfaceY + y1) * 0.5 + RAIL_POST_H * 0.5,
        (pz + bz) * 0.5,
        seg + 0.04,
        RAIL_POST_H,
        0.055,
        MAT.metal,
        Math.atan2(-(bz - pz), bx - px),
      );
      span.invisible = true;
      brushes.push(span);
    }
    addSlopedRail(parent, mats, hxTrim, hyTrim + RAIL_POST_H, hzTrim, lxSide, highY - rise + RAIL_POST_H, lzSide);
    addSlopedRail(
      parent,
      mats,
      hxTrim,
      hyTrim + RAIL_POST_H * 0.48,
      hzTrim,
      lxSide,
      highY - rise + RAIL_POST_H * 0.48,
      lzSide,
      RAIL_BAR_T * 0.85,
    );
  }
}

function createRampGeometry(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const sx = 0.5;
  const sy = 0.5;
  const sz = 0.5;
  const L0: [number, number, number] = [-sx, -sy, -sz];
  const L1: [number, number, number] = [-sx, -sy, sz];
  const Hb0: [number, number, number] = [sx, -sy, -sz];
  const Hb1: [number, number, number] = [sx, -sy, sz];
  const Ht0: [number, number, number] = [sx, sy, -sz];
  const Ht1: [number, number, number] = [sx, sy, sz];
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const pushTri = (a: [number, number, number], b: [number, number, number], c: [number, number, number]) => {
    const i = positions.length / 3;
    positions.push(...a, ...b, ...c);
    const e1x = b[0] - a[0];
    const e1y = b[1] - a[1];
    const e1z = b[2] - a[2];
    const e2x = c[0] - a[0];
    const e2y = c[1] - a[1];
    const e2z = c[2] - a[2];
    let nx = e1y * e2z - e1z * e2y;
    let ny = e1z * e2x - e1x * e2z;
    let nz = e1x * e2y - e1y * e2x;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    normals.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
    uvs.push(0, 0, 1, 0, 0.5, 1);
    indices.push(i, i + 1, i + 2);
  };
  pushTri(L0, Hb0, Hb1);
  pushTri(L0, Hb1, L1);
  pushTri(L0, Ht1, Ht0);
  pushTri(L0, L1, Ht1);
  pushTri(Hb0, Ht0, Ht1);
  pushTri(Hb0, Ht1, Hb1);
  pushTri(L0, Ht0, Hb0);
  pushTri(L1, Hb1, Ht1);
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);
  return geo;
}

const _rampGeo = createRampGeometry();

function createBrushMesh(brush: Brush, mats: ArenaMats): THREE.Mesh {
  const surf = surfForBrush(brush.id);
  const base = pickFacilityMaterial(mats, brush.id, surf);
  const uv = brush.id.startsWith('crate_')
    ? { u: 1, v: 1 }
    : brush.id.startsWith('vent_')
      ? uvRepeatForVent(
          brush.size,
          brush.id.startsWith('vent_ceil') ||
            brush.id.startsWith('vent_deck') ||
            brush.id === 'vent_arm_floor' ||
            brush.id === 'vent_arm_ceil'
            ? 'ceiling'
            : 'wall',
        )
      : uvRepeatForBrush(
          brush.size,
          brush.id.startsWith('landing') ||
            brush.id.startsWith('catwalk') ||
            brush.id.startsWith('lip_cw') ||
            brush.id.startsWith('lip_stair')
            ? 'floor'
            : uvKindForSurf(surf),
        );
  const worldWall =
    brush.kind !== 'ramp' &&
    !brush.id.startsWith('crate_') &&
    !brush.id.startsWith('vent_') &&
    !brush.id.startsWith('wall_vent') &&
    uvKindForSurf(surf) === 'wall';
  const material = cloneMatWorldUv(base, worldWall ? 1 : uv.u, worldWall ? 1 : uv.v);
  let mesh: THREE.Mesh;
  if (brush.kind === 'ramp') {
    mesh = new THREE.Mesh(_rampGeo, material);
    mesh.scale.set(brush.size.x, brush.size.y, brush.size.z);
  } else {
    const box = segmentedBox(brush.size.x, brush.size.y, brush.size.z);
    box.userData.bakeOwned = true;
    mesh = new THREE.Mesh(box, material);
  }
  mesh.position.copy(brush.center);
  mesh.rotation.y = brush.yaw;
  if (worldWall) {
    applyWorldWallUvs(mesh, brush.size.x >= brush.size.z ? 'x' : 'z');
  }
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.name = brush.id;
  if (brush.id.startsWith('crate_')) {
    mesh.userData.destructible = 'crate';
    mesh.userData.brush = brush;
    mesh.userData.crateStyle = crateStyleForId(brush.id);
    mesh.userData.crateLoot = CRATE_LOOT[brush.id] ?? null;
  } else if (brush.explodable) {
    mesh.userData.destructible = 'wall';
    mesh.userData.brush = brush;
  }
  return mesh;
}

function skipBrushMesh(id: string): boolean {
  return id.startsWith('pillar_tank') || id.startsWith('pillar_bot') || id.startsWith('vent_grate');
}

let _glassMat: THREE.MeshStandardMaterial | null = null;

function glassMaterial(): THREE.MeshStandardMaterial {
  if (!_glassMat) {
    _glassMat = new THREE.MeshStandardMaterial({
      color: 0xa8d4e4,
      metalness: 0.15,
      roughness: 0.12,
      transparent: true,
      opacity: 0.32,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
  }
  return _glassMat;
}

/**
 * Sill / lintel / piers + metal frames + destructible glass.
 * `axis: 'x'` = wall at constant X (openings along Z); `'z'` = wall at constant Z.
 * Glass has no collision — same as TestArena ops windows.
 */
function addGlassWindows(
  brushes: Brush[],
  group: THREE.Group,
  spec: {
    id: string;
    axis: 'x' | 'z';
    wall: number;
    along0: number;
    along1: number;
    floorY: number;
    height: number;
    openings: Array<{ center: number; width: number }>;
    sillH?: number;
    headH?: number;
    thick?: number;
  },
): void {
  const thick = spec.thick ?? WALL_T;
  const sillH = spec.sillH ?? 1.05;
  const headH = spec.headH ?? 0.42;
  const sillTop = spec.floorY + sillH;
  const wallTop = spec.floorY + spec.height;
  const lintelBot = wallTop - headH;
  const midH = lintelBot - sillTop;
  if (midH < 0.4) return;
  const midCy = (sillTop + lintelBot) * 0.5;
  const alongMid = (spec.along0 + spec.along1) * 0.5;
  const span = spec.along1 - spec.along0;
  const alongX = spec.axis === 'z';
  const frameT = 0.08;
  const glassT = 0.06;
  const mat = glassMaterial();

  if (alongX) {
    brushes.push(box(`${spec.id}_sill`, alongMid, spec.floorY + sillH * 0.5, spec.wall, span, sillH, thick, MAT.wall));
    brushes.push(
      box(`${spec.id}_lintel`, alongMid, (lintelBot + wallTop) * 0.5, spec.wall, span, wallTop - lintelBot, thick, MAT.wall),
    );
  } else {
    brushes.push(box(`${spec.id}_sill`, spec.wall, spec.floorY + sillH * 0.5, alongMid, thick, sillH, span, MAT.wall));
    brushes.push(
      box(`${spec.id}_lintel`, spec.wall, (lintelBot + wallTop) * 0.5, alongMid, thick, wallTop - lintelBot, span, MAT.wall),
    );
  }

  const sorted = [...spec.openings].sort((a, b) => a.center - b.center);
  let cursor = spec.along0;
  let pier = 0;

  const flushPier = (a: number, b: number) => {
    const depth = b - a;
    if (depth < 0.05) return;
    const mid = (a + b) * 0.5;
    if (alongX) {
      brushes.push(box(`${spec.id}_pier_${pier++}`, mid, midCy, spec.wall, depth, midH, thick, MAT.wall));
    } else {
      brushes.push(box(`${spec.id}_pier_${pier++}`, spec.wall, midCy, mid, thick, midH, depth, MAT.wall));
    }
  };

  for (let i = 0; i < sorted.length; i++) {
    const op = sorted[i]!;
    const o0 = Math.max(spec.along0, op.center - op.width * 0.5);
    const o1 = Math.min(spec.along1, op.center + op.width * 0.5);
    flushPier(cursor, o0);
    cursor = o1;

    const winW = o1 - o0;
    const cz = (o0 + o1) * 0.5;
    const winH = midH - frameT * 2;
    const openW = winW - frameT * 2;
    if (winW < 0.35 || winH < 0.3) continue;

    if (alongX) {
      brushes.push(box(`jamb_${spec.id}_${i}_bot`, cz, sillTop + frameT * 0.5, spec.wall, winW, frameT, thick + 0.04, MAT.metal));
      brushes.push(box(`jamb_${spec.id}_${i}_top`, cz, lintelBot - frameT * 0.5, spec.wall, winW, frameT, thick + 0.04, MAT.metal));
      brushes.push(
        box(`jamb_${spec.id}_${i}_a`, o0 + frameT * 0.5, midCy, spec.wall, frameT, midH, thick + 0.04, MAT.metal),
      );
      brushes.push(
        box(`jamb_${spec.id}_${i}_b`, o1 - frameT * 0.5, midCy, spec.wall, frameT, midH, thick + 0.04, MAT.metal),
      );
      brushes.push(box(`jamb_${spec.id}_${i}_mull`, cz, midCy, spec.wall, frameT * 0.7, midH - frameT * 2, thick + 0.02, MAT.metal));
      const paneW = (openW - frameT * 0.7) * 0.5;
      for (const side of [-1, 1] as const) {
        const pane = new THREE.Mesh(new THREE.BoxGeometry(paneW, winH, glassT), mat);
        pane.position.set(cz + side * (paneW * 0.5 + frameT * 0.35), midCy, spec.wall);
        pane.castShadow = false;
        pane.receiveShadow = false;
        pane.name = `glass_${spec.id}_${i}_${side > 0 ? 'b' : 'a'}`;
        pane.userData.destructible = 'glass';
        group.add(pane);
      }
    } else {
      brushes.push(box(`jamb_${spec.id}_${i}_bot`, spec.wall, sillTop + frameT * 0.5, cz, thick + 0.04, frameT, winW, MAT.metal));
      brushes.push(box(`jamb_${spec.id}_${i}_top`, spec.wall, lintelBot - frameT * 0.5, cz, thick + 0.04, frameT, winW, MAT.metal));
      brushes.push(
        box(`jamb_${spec.id}_${i}_a`, spec.wall, midCy, o0 + frameT * 0.5, thick + 0.04, midH, frameT, MAT.metal),
      );
      brushes.push(
        box(`jamb_${spec.id}_${i}_b`, spec.wall, midCy, o1 - frameT * 0.5, thick + 0.04, midH, frameT, MAT.metal),
      );
      brushes.push(box(`jamb_${spec.id}_${i}_mull`, spec.wall, midCy, cz, thick + 0.02, midH - frameT * 2, frameT * 0.7, MAT.metal));
      const paneW = (openW - frameT * 0.7) * 0.5;
      for (const side of [-1, 1] as const) {
        const pane = new THREE.Mesh(new THREE.BoxGeometry(glassT, winH, paneW), mat);
        pane.position.set(spec.wall, midCy, cz + side * (paneW * 0.5 + frameT * 0.35));
        pane.castShadow = false;
        pane.receiveShadow = false;
        pane.name = `glass_${spec.id}_${i}_${side > 0 ? 'b' : 'a'}`;
        pane.userData.destructible = 'glass';
        group.add(pane);
      }
    }
  }
  flushPier(cursor, spec.along1);
}

function createFluorescent(
  x: number,
  y: number,
  z: number,
  length: number,
  yaw = 0,
): { mesh: THREE.Group; bake: BakeLight } {
  const root = new THREE.Group();
  root.position.set(x, y, z);
  root.rotation.y = yaw;
  root.userData.destructible = 'lamp';
  const housing = new THREE.Mesh(
    new THREE.BoxGeometry(length, 0.08, 0.28),
    new THREE.MeshStandardMaterial({ color: 0x3a4048, roughness: 0.45, metalness: 0.7 }),
  );
  const tube = new THREE.Mesh(
    new THREE.BoxGeometry(length * 0.92, 0.05, 0.16),
    new THREE.MeshStandardMaterial({
      color: 0xe8f0ff,
      emissive: 0xddeeff,
      emissiveIntensity: 3.2,
      roughness: 0.25,
      metalness: 0.1,
    }),
  );
  tube.position.y = -0.04;
  tube.name = 'lamp_tube';
  const lip = new THREE.Mesh(
    new THREE.BoxGeometry(length * 0.9, 0.012, 0.22),
    new THREE.MeshStandardMaterial({
      color: 0xd4e2f4,
      emissive: 0xc4d8f0,
      emissiveIntensity: 0.55,
      roughness: 0.35,
      metalness: 0.04,
    }),
  );
  lip.position.y = -0.072;
  lip.name = 'lamp_lip';
  const glow = new THREE.Mesh(new THREE.PlaneGeometry(length * 0.95, 0.55), lampGlowMaterial());
  glow.rotation.x = Math.PI / 2;
  glow.position.y = -0.09;
  glow.name = 'lamp_glow';
  glow.userData.skipBake = true;
  root.add(housing, tube, lip, glow);
  root.userData.lampTube = tube;
  root.userData.lampGlow = glow;
  return {
    mesh: root,
    bake: {
      position: new THREE.Vector3(x, y - 0.35, z),
      color: new THREE.Color(0xe8f2ff),
      intensity: 1.15,
      range: 7.5,
      kind: 'lamp',
    },
  };
}

let _lampGlowMat: THREE.MeshBasicMaterial | null = null;

function lampGlowMaterial(): THREE.MeshBasicMaterial {
  if (_lampGlowMat) return _lampGlowMat.clone();
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(230, 242, 255, 0.9)');
  g.addColorStop(0.32, 'rgba(200, 220, 255, 0.38)');
  g.addColorStop(1, 'rgba(180, 205, 255, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const map = new THREE.CanvasTexture(c);
  map.colorSpace = THREE.SRGBColorSpace;
  _lampGlowMat = new THREE.MeshBasicMaterial({
    map,
    color: 0xe8f2ff,
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    fog: false,
  });
  return _lampGlowMat.clone();
}

function createAlarmBeacon(x: number, y: number, z: number): THREE.Group {
  const root = new THREE.Group();
  root.name = 'alarm_beacon';
  root.position.set(x, y, z);
  root.userData.destructible = 'lamp';
  root.userData.alarmBeacon = true;
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.09, 0.045, 10),
    new THREE.MeshStandardMaterial({ color: 0x2a2c30, roughness: 0.48, metalness: 0.72 }),
  );
  const lens = new THREE.Mesh(
    new THREE.SphereGeometry(0.07, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.72),
    new THREE.MeshStandardMaterial({
      color: 0x5a1210,
      roughness: 0.28,
      metalness: 0.12,
      emissive: 0x4a0808,
      emissiveIntensity: 0.2,
    }),
  );
  lens.rotation.x = Math.PI;
  lens.position.y = -0.055;
  lens.userData.alarmLamp = true;
  lens.userData.skipBake = true;
  root.add(base, lens);
  return root;
}

const VENT_FLOOR = WALL_H + 0.1;
const VENT_H = 1.22;
const VENT_T = 0.14;
/** Bath ceiling slab under the vent deck — hang fixtures below this, not at WALL_H. */
const BATH_CEIL_Y = VENT_FLOOR - 0.16;
const BATH_CEIL_H = 0.25;
const BATH_LAMP_Y = BATH_CEIL_Y - BATH_CEIL_H * 0.5 - 0.1;
/** Empty east stall. */
const VENT_GRATE = { x: 3.75, z: 2.3, sx: 1.04, sz: 1.04 };
/** Occupied west stall (sitter at x=−3.75). */
const VENT_GRATE_SIT = { x: -3.75, z: 1.58, sx: 1.04, sz: 1.04 };

function grateHole(g: { x: number; z: number; sx: number; sz: number }, lip = 0.08) {
  return {
    x0: g.x - g.sx * 0.5 + lip,
    x1: g.x + g.sx * 0.5 - lip,
    z0: g.z - g.sz * 0.5 + lip,
    z1: g.z + g.sz * 0.5 - lip,
  };
}

function addVentOverBath(brushes: Brush[], rooms: FacilityRoom[], group: THREE.Group, mats: ArenaMats): void {
  const east = grateHole(VENT_GRATE);
  const west = grateHole(VENT_GRATE_SIT);

  // Sit clearly under the vent deck so the darker bath ceiling never shares a plane.
  const ceil = (id: string, ax: number, az: number, bx: number, bz: number) => {
    brushes.push(box(id, (ax + bx) * 0.5, BATH_CEIL_Y, (az + bz) * 0.5, Math.abs(bx - ax), BATH_CEIL_H, Math.abs(bz - az), MAT.metal));
  };
  ceil('ceil_bath_far_w', -5, 0, west.x0, 11);
  ceil('ceil_bath_mid', west.x1, 0, east.x0, 11);
  ceil('ceil_bath_far_e', east.x1, 0, 5, 11);
  ceil('ceil_bath_east_n', east.x0, east.z1, east.x1, 11);
  ceil('ceil_bath_east_s', east.x0, 0, east.x1, east.z0);
  ceil('ceil_bath_west_n', west.x0, west.z1, west.x1, 11);
  ceil('ceil_bath_west_s', west.x0, 0, west.x1, west.z0);

  const n0 = -0.61;
  const n1 = 0.61;
  const e0 = 3.22;
  const e1 = 4.44;
  const w0 = -4.44;
  const w1 = -3.22;
  const zStart = 7.15;
  const zElbow0 = 4.15;
  const zElbow1 = 5.37;
  const zEndE = 1.7;
  const zEndW = 1.06;

  rooms.push({
    id: 'vent',
    x0: w0 - 0.25,
    z0: zEndW - 0.2,
    x1: e1 + 0.25,
    z1: 9.9,
    floorY: VENT_FLOOR,
    height: VENT_H,
    gaps: [],
    sparse: true,
    see: ['bath'],
  });
  const bath = rooms.find((r) => r.id === 'bath');
  if (bath) bath.see = ['vent'];

  const T = VENT_T;
  const shaftX0 = n0;
  const shaftX1 = n1;
  const shaftZ0 = 6.12;
  const shaftZ1 = 6.98;
  const wy = wallCy(VENT_FLOOR, VENT_H);
  const wall = (id: string, x: number, z: number, sx: number, sz: number) => {
    brushes.push(box(id, x, wy, z, sx, VENT_H, sz, MAT.metal));
  };
  const slab = (id: string, ax: number, az: number, bx: number, bz: number) => {
    brushes.push(
      box(id, (ax + bx) * 0.5, VENT_FLOOR + VENT_H + 0.08, (az + bz) * 0.5, Math.abs(bx - ax), 0.16, Math.abs(bz - az), MAT.metal),
    );
  };
  const deck = (id: string, ax: number, az: number, bx: number, bz: number) => {
    const sx = Math.abs(bx - ax);
    const sz = Math.abs(bz - az);
    if (sx < 0.04 || sz < 0.04) return;
    // Top at VENT_FLOOR+0.05; bottom above the bath ceiling so xpanel never fights metal.
    brushes.push(box(id, (ax + bx) * 0.5, VENT_FLOOR + 0.035, (az + bz) * 0.5, sx, 0.04, sz, MAT.metal));
  };

  // NS runs own the corners (through end-caps). EW runs stop at those outers.
  // Split at the look-up hole so that segment can extrude to the shaft top.
  wall('vent_wall_start_w', n0 - T * 0.5, (zElbow1 + shaftZ0) * 0.5, T, shaftZ0 - zElbow1);
  wall('vent_wall_start_e', n1 + T * 0.5, (zElbow1 + shaftZ0) * 0.5, T, shaftZ0 - zElbow1);
  wall('vent_wall_start_w_n', n0 - T * 0.5, (shaftZ1 + zStart + T) * 0.5, T, zStart + T - shaftZ1);
  wall('vent_wall_start_e_n', n1 + T * 0.5, (shaftZ1 + zStart + T) * 0.5, T, zStart + T - shaftZ1);
  wall('vent_wall_cap_n', (n0 + n1) * 0.5, zStart + T * 0.5, n1 - n0, T);
  wall('vent_wall_ew_n_w', (w0 - T + n0 - T) * 0.5, zElbow1 + T * 0.5, n0 - w0, T);
  wall('vent_wall_ew_n_e', (n1 + T + e1 + T) * 0.5, zElbow1 + T * 0.5, e1 - n1, T);
  wall('vent_wall_ew_s', (w1 + T + e0 - T) * 0.5, zElbow0 - T * 0.5, e0 - w1 - 2 * T, T);
  wall('vent_wall_east_w', e0 - T * 0.5, (zEndE - T + zElbow0) * 0.5, T, zElbow0 - (zEndE - T));
  wall('vent_wall_east_e', e1 + T * 0.5, (zEndE - T + zElbow1 + T) * 0.5, T, zElbow1 - zEndE + 2 * T);
  wall('vent_wall_cap_e', (e0 + e1) * 0.5, zEndE - T * 0.5, e1 - e0, T);
  wall('vent_wall_west_e', w1 + T * 0.5, (zEndW - T + zElbow0) * 0.5, T, zElbow0 - (zEndW - T));
  wall('vent_wall_west_w', w0 - T * 0.5, (zEndW - T + zElbow1 + T) * 0.5, T, zElbow1 - zEndW + 2 * T);
  wall('vent_wall_cap_w', (w0 + w1) * 0.5, zEndW - T * 0.5, w1 - w0, T);

  // One slab through the spawn T so ceiling end-caps do not share a plane.
  slab('vent_ceil_ns_s', n0, zElbow0, n1, shaftZ0);
  slab('vent_ceil_ns_n', n0, shaftZ1, n1, zStart);
  slab('vent_ceil_ew_w', w1, zElbow0, n0, zElbow1);
  slab('vent_ceil_ew_e', n1, zElbow0, e0, zElbow1);
  slab('vent_ceil_east', e0, zEndE, e1, zElbow1);
  slab('vent_ceil_west', w0, zEndW, w1, zElbow1);

  const plugBottom = VENT_FLOOR + VENT_H + 0.18;
  const plugH = 1.65;
  const plug = box(
    'vent_shaft_plug',
    (shaftX0 + shaftX1) * 0.5,
    plugBottom + plugH * 0.5,
    (shaftZ0 + shaftZ1) * 0.5,
    shaftX1 - shaftX0,
    plugH,
    shaftZ1 - shaftZ0,
    MAT.metal,
  );
  plug.invisible = true;
  brushes.push(plug);
  const shaftH = 1.7;
  const armFloorY = plugBottom + shaftH;
  const armH = VENT_H;
  const chimneyTop = armFloorY + armH;
  const holeH = chimneyTop - VENT_FLOOR;
  const holeCy = VENT_FLOOR + holeH * 0.5;
  const holeZ = (shaftZ0 + shaftZ1) * 0.5;
  const holeSz = shaftZ1 - shaftZ0;
  brushes.push(box('vent_shaft_w', shaftX0 - T * 0.5, holeCy, holeZ, T, holeH, holeSz, MAT.metal));
  brushes.push(box('vent_shaft_e', shaftX1 + T * 0.5, holeCy, holeZ, T, holeH, holeSz, MAT.metal));
  const rimBottom = VENT_FLOOR + VENT_H;
  const southH = chimneyTop - rimBottom;
  brushes.push(
    box('vent_shaft_s', (shaftX0 + shaftX1) * 0.5, rimBottom + southH * 0.5, shaftZ0 + T * 0.5, shaftX1 - shaftX0, southH, T, MAT.metal),
  );
  const northH = armFloorY - rimBottom;
  brushes.push(
    box('vent_shaft_n', (shaftX0 + shaftX1) * 0.5, rimBottom + northH * 0.5, shaftZ1 - T * 0.5, shaftX1 - shaftX0, northH, T, MAT.metal),
  );

  const armZ0 = shaftZ1;
  const armZ1 = 9.55;
  const armCy = armFloorY + armH * 0.5;
  const armWall = (id: string, x: number, z: number, sx: number, sz: number) => {
    brushes.push(box(id, x, armCy, z, sx, armH, sz, MAT.metal));
  };
  armWall('vent_arm_w', shaftX0 - VENT_T * 0.5, (armZ0 + armZ1) * 0.5, VENT_T, armZ1 - armZ0);
  armWall('vent_arm_e', shaftX1 + VENT_T * 0.5, (armZ0 + armZ1) * 0.5, VENT_T, armZ1 - armZ0);
  armWall('vent_arm_n', (shaftX0 + shaftX1) * 0.5, armZ1 + VENT_T * 0.5, shaftX1 - shaftX0, VENT_T);
  brushes.push(
    box(
      'vent_arm_floor',
      (shaftX0 + shaftX1) * 0.5,
      armFloorY + 0.025,
      (armZ0 + armZ1) * 0.5,
      shaftX1 - shaftX0,
      0.05,
      armZ1 - armZ0,
      MAT.metal,
    ),
  );
  brushes.push(
    box(
      'vent_arm_ceil',
      (shaftX0 + shaftX1) * 0.5,
      armFloorY + armH + 0.08,
      (shaftZ0 + armZ1) * 0.5,
      shaftX1 - shaftX0 + VENT_T,
      0.16,
      armZ1 - shaftZ0 + VENT_T,
      MAT.metal,
    ),
  );

  deck('vent_deck_ns', n0 - T, zElbow0, n1 + T, zStart);
  deck('vent_deck_ew_w', w0 - T, zElbow0, n0 - T, zElbow1);
  deck('vent_deck_ew_e', n1 + T, zElbow0, e1 + T, zElbow1);
  deck('vent_deck_east_w', e0 - T, zEndE, east.x0, zElbow0);
  deck('vent_deck_east_e', east.x1, zEndE, e1 + T, zElbow0);
  deck('vent_deck_east_n', east.x0, east.z1, east.x1, zElbow0);
  deck('vent_deck_east_s', east.x0, zEndE, east.x1, east.z0);
  deck('vent_deck_west_w', w0 - T, zEndW, west.x0, zElbow0);
  deck('vent_deck_west_e', west.x1, zEndW, w1 + T, zElbow0);
  deck('vent_deck_west_n', west.x0, west.z1, west.x1, zElbow0);
  deck('vent_deck_west_s', west.x0, zEndW, west.x1, west.z0);

  placeStallGrate(brushes, group, mats, 'vent_grate', VENT_GRATE, east, BATH_CEIL_Y);
  placeStallGrate(brushes, group, mats, 'vent_grate_sit', VENT_GRATE_SIT, west, BATH_CEIL_Y);
}

function placeStallGrate(
  brushes: Brush[],
  group: THREE.Group,
  mats: ArenaMats,
  id: string,
  spec: { x: number; z: number; sx: number; sz: number },
  hole: { x0: number; x1: number; z0: number; z1: number },
  ceilY: number,
): void {
  const grate = box(id, spec.x, VENT_FLOOR - 0.07, spec.z, spec.sx, 0.05, spec.sz, MAT.metal);
  brushes.push(grate);
  const obj = addVentGrate(group, mats, grate);
  addVentGrateLip(group, mats, hole.x0, hole.x1, hole.z0, hole.z1, ceilY - 0.13);
  obj.userData.climbDest = new THREE.Vector3(spec.x, VENT_FLOOR + 0.08, hole.z1 + 0.22);
}

function addVentGrate(group: THREE.Group, mats: ArenaMats, brush: Brush): THREE.Group {
  const g = new THREE.Group();
  g.name = 'vent_grate';
  g.position.copy(brush.center);
  g.userData.destructible = 'grate';
  g.userData.interact = 'grate';
  g.userData.brush = brush;
  const steel = mats.vent;
  const dark = new THREE.MeshStandardMaterial({
    color: 0x2a322c,
    metalness: 0.82,
    roughness: 0.4,
  });
  const sx = brush.size.x;
  const sz = brush.size.z;
  const hinge = new THREE.Group();
  hinge.name = 'vent_grate_hinge';
  hinge.position.set(0, 0, -sz * 0.5);
  const leaf = new THREE.Group();
  leaf.name = 'vent_grate_leaf';
  leaf.position.set(0, 0, sz * 0.5);
  const add = (mat: THREE.Material, px: number, py: number, pz: number, wx: number, wy: number, wz: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(wx, wy, wz), mat);
    m.position.set(px, py, pz);
    m.castShadow = true;
    m.receiveShadow = true;
    m.userData.destructible = 'grate';
    leaf.add(m);
  };
  const frameT = 0.06;
  add(dark, 0, 0, -sz * 0.5, sx, 0.055, frameT);
  add(dark, 0, 0, sz * 0.5, sx, 0.055, frameT);
  add(dark, -sx * 0.5, 0, 0, frameT, 0.055, sz);
  add(dark, sx * 0.5, 0, 0, frameT, 0.055, sz);
  for (let i = -2; i <= 2; i++) add(steel, (i * sx) / 5.4, 0, 0, 0.032, 0.04, sz - 0.1);
  for (let i = -2; i <= 2; i++) add(steel, 0, 0, (i * sz) / 5.4, sx - 0.1, 0.04, 0.032);
  const hit = new THREE.Mesh(
    new THREE.BoxGeometry(sx, 0.08, sz),
    new THREE.MeshBasicMaterial({ visible: false }),
  );
  hit.userData.destructible = 'grate';
  hit.userData.interact = 'grate';
  leaf.add(hit);
  hinge.add(leaf);
  g.add(hinge);
  group.add(g);
  return g;
}

function addVentGrateLip(
  group: THREE.Group,
  mats: ArenaMats,
  hx0: number,
  hx1: number,
  hz0: number,
  hz1: number,
  y: number,
): void {
  const steel = mats.vent;
  const t = 0.055;
  const add = (px: number, pz: number, wx: number, wz: number) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(wx, 0.045, wz), steel);
    m.position.set(px, y, pz);
    m.castShadow = true;
    m.receiveShadow = true;
    group.add(m);
  };
  const mx = (hx0 + hx1) * 0.5;
  const mz = (hz0 + hz1) * 0.5;
  const sx = hx1 - hx0;
  const sz = hz1 - hz0;
  add(mx, hz0, sx + t, t);
  add(mx, hz1, sx + t, t);
  add(hx0, mz, t, sz);
  add(hx1, mz, t, sz);
}

/**
 * Multi-floor facility: bathroom → foyer (down to tanks / north to security)
 * → hub → bottling, with stairs up to labs and a catwalk drop into bottling.
 */
export function buildFacilityArena(): FacilityArenaBuild {
  const brushes: Brush[] = [];
  const lights: THREE.Light[] = [];
  const bakeLights: BakeLight[] = [];
  const doors: DoorDesc[] = [];
  const rooms: FacilityRoom[] = [];
  const mats = createArenaMaterials();
  const group = new THREE.Group();
  group.name = 'facility_arena';

  const safety = box('safety_floor', 3, -8, 37, 70, 1, 90, MAT.floor);
  safety.invisible = true;
  brushes.push(safety);

  // ── Bathroom (start) ──────────────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'bath',
      x0: -5,
      z0: 0,
      x1: 5,
      z1: 11,
      floorY: Y0,
      height: WALL_H,
      gaps: [{ side: 'n', along: 0 }],
    },
    { skipWall: { n: true }, ceiling: false },
  );
  addWallWithDoorX(brushes, 'wall_bath_n', 11, -5, 5, 0);
  doors.push({ ...doorOnXWall('bath', 0, 11, 1), style: 'bath' });
  for (const [i, x] of [-3.75, -1.25, 1.25, 3.75].entries()) {
    doors.push(stallDoor(`stall_${i}`, x));
  }
  // Invisible collision — matches dressing (sides run flush with the stall front).
  const stallBackZ = 0.32;
  const stallFrontZ = 3.845;
  const stallSideEnd = 3.875;
  const stallDepth = stallSideEnd - stallBackZ;
  const stallMidZ = (stallSideEnd + stallBackZ) * 0.5;
  for (const [id, x] of [
    ['stall_1', -2.5],
    ['stall_2', 0],
    ['stall_3', 2.5],
  ] as const) {
    const stall = box(id, x, 1.07, stallMidZ, 0.12, 1.86, stallDepth, MAT.metal);
    stall.invisible = true;
    brushes.push(stall);
  }
  const stallEdges = [-4.7, -2.5, 0, 2.5, 4.7];
  const stallBays = [-3.75, -1.25, 1.25, 3.75];
  const stallPost = 0.5;
  stallBays.forEach((cx, i) => {
    const left0 = stallEdges[i] + (i > 0 ? 0.046 : 0);
    const left1 = cx - stallPost - 0.03;
    const right0 = cx + stallPost + 0.03;
    const right1 = stallEdges[i + 1] - (i < stallBays.length - 1 ? 0.046 : 0);
    const left = box(
      `stall_front_${i}l`,
      (left0 + left1) * 0.5,
      1.07,
      stallFrontZ,
      Math.max(0.08, left1 - left0),
      1.86,
      0.07,
      MAT.metal,
    );
    const right = box(
      `stall_front_${i}r`,
      (right0 + right1) * 0.5,
      1.07,
      stallFrontZ,
      Math.max(0.08, right1 - right0),
      1.86,
      0.07,
      MAT.metal,
    );
    left.invisible = true;
    right.invisible = true;
    brushes.push(left, right);
  });
  addVentOverBath(brushes, rooms, group, mats);

  // ── Foyer ─────────────────────────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'foyer',
      x0: -6,
      z0: 11,
      x1: 6,
      z1: 21,
      floorY: Y0,
      height: WALL_H,
      gaps: [
        { side: 's', along: 0 },
        { side: 'n', along: 0 },
        { side: 'w', along: 16, width: OPEN_W },
      ],
    },
    { skipWall: { n: true, s: true, w: true } },
  );
  plugWidthGap(brushes, 'wall_foyer_s_plug', 11, -5, 5, -6, 6);
  addWallWithDoorX(brushes, 'wall_foyer_n', 21, -6, 6, 0);
  doors.push(doorOnXWall('foyer_sec', 0, 21, 1));
  addWallWithDoorZ(brushes, 'wall_foyer_w', -6, 11, 21, 16, Y0, WALL_H, OPEN_W);
  const floorFoyer = brushes.find((b) => b.id === 'floor_foyer');
  if (floorFoyer) {
    // Stop at the inner face of the west wall — do not cover the stair lip.
    floorFoyer.center.set(0.12, Y0 - 0.2, 16);
    floorFoyer.size.set(11.76, 0.4, 9.7);
  }

  // ── Stairwell down (west of foyer) ────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'stairdn',
      x0: -16,
      z0: 11,
      x1: -6,
      z1: 21,
      floorY: YB,
      height: H_SHAFT_DN,
      gaps: [
        { side: 'e', along: 16, width: OPEN_W },
        { side: 'n', along: -13.7, width: 3.0 },
        { side: 's', along: -11.2 },
        { side: 'w', along: 13.4 },
      ],
      sparse: true,
    },
    { skipWall: { e: true, n: true, s: true, w: true } },
  );
  // One full-height east wall; opening only at foyer walkway (sill fills the pit).
  // Stop the sill shy of y=0 so it does not share a plane with lip_stairdn.
  addWallWithDoorZ(brushes, 'wall_stairdn_e', -6.42, 10.82, 20.62, 16, YB, H_SHAFT_DN, OPEN_W, DOOR_H, -YB - 0.08);
  // Opening is basement-tall only — a full-shaft cut shows the void above the tanks ceiling.
  addWallWithDoorX(brushes, 'wall_stairdn_n', 20.58, -16.25, -6.18, -13.45, YB, H_SHAFT_DN, 3.5, WALL_H - 0.32);
  addWallWithDoorX(brushes, 'wall_stairdn_s', 11, -16.25, -6.18, -11.2, YB, H_SHAFT_DN);
  doors.push(doorOnXWall('stair_stash_s', -11.2, 11, -1, YB));
  addWallWithDoorZ(brushes, 'wall_stairdn_w', -16, 10.88, 20.58, 13.4, YB, H_SHAFT_DN);
  doors.push(doorOnZWall('stair_stash_w', -16, 13.4, -1, YB));

  // Threshold: cover the lowered sill (x −6.62..−6.22) and stop before the foyer slab (−5.76).
  addFloorSlab(brushes, 'lip_stairdn', -6.66, 14.75, -5.78, 17.25, Y0);
  addGuardRail(brushes, 'lipdn_n', -7.025, 17.14, -6.075, 17.14, Y0);
  addGuardRail(brushes, 'lipdn_s', -7.025, 14.86, -6.075, 14.86, Y0);
  addStairFlight(brushes, group, mats, 'dn1', -6.55, 16, 0, 5.2, 1.75, 2.4, Y0);
  addFloorSlab(brushes, 'landing_dn', -15.72, 14.88, -11.5, 17.12, YB + 1.75);
  addGuardRail(brushes, 'landdn_s', -15.7, 14.86, -11.55, 14.86, YB + 1.75);
  // Stay inside the shaft and in the north opening (x −15.2..−11.7, wall z=20.58).
  // The old west-hug ran the flight through the tank jamb.
  addStairFlight(brushes, group, mats, 'dn2', -14.29, 16.2, Math.PI * 0.5, 3.88, 1.75, 1.62, YB + 1.75, {
    a: true,
    b: false,
  });
  const dn2WallPlug = box(
    'block_dn2_wall',
    -15.52,
    YB + H_SHAFT_DN * 0.5,
    18.15,
    0.42,
    H_SHAFT_DN,
    3.7,
    MAT.wall,
  );
  dn2WallPlug.invisible = true;
  brushes.push(dn2WallPlug);

  const floorSd = brushes.find((b) => b.id === 'floor_stairdn');
  if (floorSd) {
    floorSd.center.set(-11.0, YB - 0.2, 16.0);
    floorSd.size.set(10.3, 0.4, 10.3);
  }
  const ceilSd = brushes.find((b) => b.id === 'ceil_stairdn');
  if (ceilSd) {
    ceilSd.center.set(-11.0, YB + H_SHAFT_DN + 0.12, 16.0);
    ceilSd.size.set(10.5, 0.28, 10.5);
  }

  // ── Tank hall (basement) ──────────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'tanks',
      x0: -22,
      z0: 21,
      x1: -9,
      z1: 42,
      floorY: YB,
      height: WALL_H,
      gaps: [{ side: 's', along: -13.45, width: 3.5 }],
    },
    { skipWall: { s: true } },
  );
  brushes.push(
    box('wall_tanks_s_w', -19, wallCy(YB, WALL_H), 21, 6, WALL_H, WALL_T, MAT.wall),
  );
  const floorTk = brushes.find((b) => b.id === 'floor_tanks');
  if (floorTk) {
    const z1 = floorTk.center.z + floorTk.size.z * 0.5;
    floorTk.center.z = (21.12 + z1) * 0.5;
    floorTk.size.z = z1 - 21.12;
  }
  brushes.push(box('pillar_tank_a', -16.5, YB + 1.35, 28, 1.6, 2.7, 1.6, MAT.metal));
  brushes.push(box('pillar_tank_b', -12.8, YB + 1.35, 35, 1.6, 2.7, 1.6, MAT.metal));
  brushes.push(box('crate_tank1', -19.5, YB + 0.5, 24.5, 1.4, 1.0, 1.2, MAT.accent));
  brushes.push(box('sandbag_tank', -19.2, YB + 0.35, 38.5, 1.8, 0.55, 0.7, MAT.accent));
  brushes.push(box('crate_tank2', -11.2, YB + 0.45, 40.2, 1.2, 0.9, 1.0, MAT.accent));

  // ── Hidden stash (left of the down-stair: south + west) ───────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'stash_s',
      x0: -16,
      z0: 3.2,
      x1: -6,
      z1: 11,
      floorY: YB,
      height: WALL_H,
      gaps: [{ side: 'n', along: -11.2 }, { side: 'w', along: 7 }],
      sparse: true,
    },
    { skipWall: { n: true, w: true } },
  );
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'stash_w',
      x0: -22,
      z0: 3.2,
      x1: -16,
      z1: 21,
      floorY: YB,
      height: WALL_H,
      gaps: [{ side: 'e', along: 13.4 }, { side: 'e', along: 7, width: 7.6 }],
      sparse: true,
    },
    { skipWall: { e: true, n: true } },
  );
  brushes.push(box('crate_stash_s1', -8.4, YB + 0.5, 5.5, 1.35, 1.0, 1.2, MAT.accent));
  brushes.push(box('crate_stash_s2', -13.5, YB + 0.45, 4.8, 1.2, 0.9, 1.05, MAT.accent));
  brushes.push(box('sandbag_stash_s', -9.4, YB + 0.35, 8.0, 1.8, 0.55, 0.7, MAT.accent));
  brushes.push(box('crate_stash_w1', -19.8, YB + 0.5, 16.6, 1.35, 1.0, 1.2, MAT.accent));
  brushes.push(box('crate_stash_w2', -20.1, YB + 0.45, 7.6, 1.2, 0.9, 1.05, MAT.accent));
  brushes.push(box('sandbag_stash_w', -18.2, YB + 0.35, 13.6, 1.7, 0.55, 0.7, MAT.accent));
  brushes.push(box('cabinet_stash_s', -7.1, YB + 1.05, 4.0, 0.8, 2.1, 0.4, MAT.metal, 0.15));
  brushes.push(box('cabinet_stash_w', -21.1, YB + 1.05, 18.6, 0.8, 2.1, 0.4, MAT.metal, -0.2));

  // ── Security ──────────────────────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'sec',
      x0: -9,
      z0: 21,
      x1: 9,
      z1: 35,
      floorY: Y0,
      height: WALL_H,
      gaps: [
        { side: 's', along: 0 },
        { side: 'n', along: 0 },
        { side: 'e', along: 28 },
      ],
    },
    { skipWall: { n: true, s: true, e: true } },
  );
  plugWidthGap(brushes, 'wall_sec_s_plug', 21, -6, 6, -9, 9);
  addWallWithDoorX(brushes, 'wall_sec_n', 35, -9, 9, 0);
  doors.push(doorOnXWall('sec_hub', 0, 35, 1));
  addWallWithDoorZ(brushes, 'wall_sec_e', 9, 27.25, 28.75, 28);
  doors.push(doorOnZWall('sec_wing', 9, 28, 1));
  addGlassWindows(brushes, group, {
    id: 'win_sec_s',
    axis: 'x',
    wall: 9,
    along0: 21,
    along1: 27.25,
    floorY: Y0,
    height: WALL_H,
    openings: [{ center: 24.1, width: 2.6 }],
  });
  addGlassWindows(brushes, group, {
    id: 'win_sec_n',
    axis: 'x',
    wall: 9,
    along0: 28.75,
    along1: 35,
    floorY: Y0,
    height: WALL_H,
    openings: [
      { center: 30.5, width: 2.15 },
      { center: 33.35, width: 2.15 },
    ],
  });
  brushes.push(box('desk_sec1', -3.5, 0.55, 27, 2.6, 0.85, 1.05, MAT.desk));
  brushes.push(box('crate_sec1', 5.5, 0.45, 24.2, 1.3, 0.9, 1.15, MAT.accent));
  brushes.push(box('sandbag_sec1', 6.2, 0.35, 32.2, 2.0, 0.55, 0.75, MAT.accent));
  brushes.push(box('crate_sec2', -7.2, 0.45, 32.8, 1.2, 0.9, 1.0, MAT.accent));

  // ── East wing (GE security right-door annex) ──────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'wing',
      x0: 9,
      z0: 21,
      x1: 20,
      z1: 35,
      floorY: Y0,
      height: WALL_H,
      gaps: [
        { side: 'w', along: 28 },
        { side: 'n', along: 13 },
        { side: 'e', along: 28 },
      ],
    },
    { skipWall: { w: true, n: true, e: true } },
  );
  addWallWithDoorX(brushes, 'wall_stairup_s', 35, 10.2, 16, 13, Y0, WALL_H);
  doors.push(doorOnXWall('wing_stair', 13, 35, -1));
  addWallWithDoorZ(brushes, 'wall_wing_e', 20, 21, 35, 28);
  doors.push(doorOnZWall('wing_kitchen', 20, 28, 1));
  brushes.push(box('desk_wing', 15.5, 0.55, 24.5, 2.4, 0.85, 1.0, MAT.desk));
  brushes.push(box('cabinet_wing', 18.8, 1.1, 23.2, 0.75, 2.1, 1.4, MAT.metal));
  brushes.push(box('crate_wing', 11.2, 0.45, 32.5, 1.2, 0.9, 1.0, MAT.accent));

  // ── Hub ───────────────────────────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'hub',
      x0: -10,
      z0: 35,
      x1: 10,
      z1: 51,
      floorY: Y0,
      height: WALL_H,
      gaps: [
        { side: 's', along: 0 },
        { side: 'n', along: 0 },
        { side: 'e', along: 43, width: OPEN_W },
      ],
    },
    { skipWall: { n: true, s: true, e: true } },
  );
  // East strip (x=9–10) is the stair door wall — do not double it.
  plugWidthGap(brushes, 'wall_hub_s_plug', 35, -9, 9, -10, 9.8);
  addWallWithDoorX(brushes, 'wall_hub_n', 51, -10, 10, 0);
  doors.push(doorOnXWall('hub_bottle', 0, 51, 1));
  addWallWithDoorZ(brushes, 'wall_hub_e', 10, 35, 51, 43, Y0, WALL_H, OPEN_W);
  brushes.push(box('desk_hub_l', -5.2, 0.55, 42, 2.4, 0.85, 1.0, MAT.desk));
  brushes.push(box('desk_hub_r', 5.0, 0.55, 42, 2.4, 0.85, 1.0, MAT.desk));
  brushes.push(box('cabinet_hub', 8.4, 1.1, 38.2, 0.75, 2.1, 1.5, MAT.metal));
  brushes.push(box('crate_hub', -8.2, 0.45, 48.2, 1.3, 0.9, 1.1, MAT.accent));
  brushes.push(box('sandbag_hub', -7.5, 0.35, 37.5, 1.8, 0.55, 0.7, MAT.accent));

  // ── Stairwell up (east of hub) ────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'stairup',
      x0: 10,
      z0: 35,
      x1: 16,
      z1: 51,
      floorY: Y0,
      height: H_STAIRUP,
      gaps: [
        { side: 'w', along: 43, width: OPEN_W },
        { side: 'e', along: 43, width: 3.0 },
        { side: 'e', along: 38 },
        { side: 'e', along: 48 },
        { side: 's', along: 13 },
      ],
      sparse: true,
    },
    { skipWall: { w: true, e: true, s: true, n: true }, ceiling: false },
  );
  // Keep slabs inside the shaft so they do not share a plane with hub / lab / lockers.
  const stairX0 = 10 + WALL_T * 0.5;
  const stairX1 = 15.55 - WALL_T * 0.5;
  const floorSu = brushes.find((b) => b.id === 'floor_stairup');
  if (floorSu) {
    floorSu.center.x = (stairX0 + stairX1) * 0.5;
    floorSu.center.z = 43.1;
    floorSu.size.x = stairX1 - stairX0;
    floorSu.size.z = 15.6;
  }
  const floorHub = brushes.find((b) => b.id === 'floor_hub');
  if (floorHub) {
    floorHub.center.x = (-10 + 9.8) * 0.5;
    floorHub.size.x = 19.8;
  }
  brushes.push(
    box(
      'ceil_stairup',
      (stairX0 + stairX1) * 0.5,
      Y0 + H_STAIRUP + 0.1,
      43,
      stairX1 - stairX0,
      0.25,
      15.6,
      MAT.metal,
    ),
  );
  // Start east of the bottle south plug (x 10–12) so the two z=51 faces do not overlap.
  const stairN0 = 12 + WALL_T * 0.5;
  brushes.push(
    box(
      'wall_stairup_n',
      (stairN0 + 15.75) * 0.5,
      wallCy(Y0, H_STAIRUP),
      51,
      15.75 - stairN0,
      H_STAIRUP,
      WALL_T,
      MAT.wall,
    ),
  );
  // Close the shaft above the short annex walls so lab plaster cannot show through.
  const hiH = H_STAIRUP - H_ANNEX;
  const hiCy = H_ANNEX + hiH * 0.5;
  brushes.push(box('wall_stairup_e_hi_s', 15.55, hiCy, (35 + 41.5) * 0.5, WALL_T, hiH, 6.5, MAT.wall));
  brushes.push(box('wall_stairup_e_hi_n', 15.55, hiCy, (44.5 + 51) * 0.5, WALL_T, hiH, 6.5, MAT.wall));
  brushes.push(box('wall_stairup_s_hi', (stairX0 + stairX1) * 0.5, hiCy, 35, stairX1 - stairX0, hiH, WALL_T, MAT.wall));
  const wHi0 = WALL_H + 0.22;
  const wHiH = H_STAIRUP - wHi0;
  brushes.push(box('wall_stairup_w_hi', 10, wHi0 + wHiH * 0.5, 43, WALL_T, wHiH, 16, MAT.wall));
  addFloorSlab(brushes, 'lip_stairup', 10.2, 41.75, 12.4, 44.25, Y0);
  addGuardRail(brushes, 'lipup_n', 10.25, 44.14, 12.35, 44.14, Y0);
  addGuardRail(brushes, 'lipup_s', 10.25, 41.86, 12.35, 41.86, Y0);
  addStairFlight(brushes, group, mats, 'up1', 15.55, 43, 0, 5.0, 1.8, 2.4, 1.8);
  addFloorSlab(brushes, 'landing_up', 14.7, 41.75, 15.5, 44.25, 1.8);
  addGuardRail(brushes, 'landup_n', 14.7, 44.14, 15.5, 44.14, 1.8);
  addGuardRail(brushes, 'landup_s', 14.7, 41.86, 15.5, 41.86, 1.8);
  addStairFlight(brushes, group, mats, 'up2', 20.95, 43, 0, 5.2, 1.8, 2.4, YU);
  // Inset east plugs (x=15.55) so they do not share a plane with the lab west walls.
  addWallWithDoorZ(brushes, 'wall_stairup_e_s', 15.55, 35, 41.5, 38, Y0, H_ANNEX);
  doors.push(doorOnZWall('stair_lockers', 15.55, 38, 1));
  addWallWithDoorZ(brushes, 'wall_stairup_e_n', 15.55, 44.5, 51, 48, Y0, H_ANNEX);
  doors.push(doorOnZWall('stair_lockern', 15.55, 48, 1));
  brushes.push(box('wall_stairup_hole_s', 18.6, H_ANNEX * 0.5, 41.5, 5.3, H_ANNEX, 0.2, MAT.wall));
  brushes.push(box('wall_stairup_hole_n', 18.6, H_ANNEX * 0.5, 44.5, 5.3, H_ANNEX, 0.2, MAT.wall));

  // ── Kitchen (east of wing) ────────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'kitchen',
      x0: 20,
      z0: 21,
      x1: 32,
      z1: ANNEX_DOOR_Z,
      floorY: Y0,
      height: WALL_H,
      gaps: [
        { side: 'w', along: 28 },
        { side: 'n', along: 26 },
      ],
    },
    { skipWall: { w: true, n: true } },
  );
  addWallWithDoorX(brushes, 'wall_kitchen_n', ANNEX_DOOR_Z, 21, 32, 26);
  doors.push(doorOnXWall('kitchen_gen', 26, ANNEX_DOOR_Z, 1));
  const kitchenTable = box('desk_kitchen', 24.2, 0.22, 26.5, 1.8, 0.44, 1.1, MAT.desk);
  kitchenTable.invisible = true;
  brushes.push(kitchenTable);
  const kitchenCounter = box('counter_kitchen', 30.6, 0.48, 28, 1.4, 0.96, 8.5, MAT.desk);
  kitchenCounter.invisible = true;
  brushes.push(kitchenCounter);

  // ── Generator (under labs) + locker pockets ───────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'gen',
      x0: 21,
      z0: ANNEX_DOOR_Z,
      x1: 32,
      z1: 51,
      floorY: Y0,
      height: H_ANNEX,
      gaps: [
        { side: 's', along: 26 },
        { side: 'w', along: 38 },
        { side: 'w', along: 48 },
      ],
      sparse: true,
    },
    { skipWall: { s: true, w: true }, ceiling: false },
  );
  addWallWithDoorZ(brushes, 'wall_gen_w_s', 21, ANNEX_DOOR_Z, 41.5, 38, Y0, H_ANNEX);
  doors.push(doorOnZWall('lockers_gen', 21, 38, 1));
  addWallWithDoorZ(brushes, 'wall_gen_w_n', 21, 44.5, 51, 48, Y0, H_ANNEX);
  doors.push(doorOnZWall('lockern_gen', 21, 48, 1));
  brushes.push(box('wall_gen_w_hole', 21, H_ANNEX * 0.5, 43, WALL_T, H_ANNEX, 3.0, MAT.wall));

  addRoomShell(
    brushes,
    rooms,
    {
      id: 'lockers',
      x0: 15.75,
      z0: 35,
      x1: 21,
      z1: 41.5,
      floorY: Y0,
      height: H_ANNEX,
      gaps: [
        { side: 'w', along: 38 },
        { side: 'e', along: 38 },
      ],
      sparse: true,
    },
    { skipWall: { n: true, s: true, w: true, e: true }, ceiling: false },
  );
  brushes.push(box('wall_lockers_s', 18.375, H_ANNEX * 0.5, ANNEX_DOOR_Z, 5.25, H_ANNEX, WALL_T, MAT.wall));
  brushes.push(box('cabinet_lockers_a', 17.2, 1.05, 37.2, 0.85, 2.1, 0.42, MAT.metal));
  brushes.push(box('cabinet_lockers_b', 19.4, 1.05, 37.2, 0.85, 2.1, 0.42, MAT.metal));
  brushes.push(box('cabinet_lockers_w_s', 16.02, 1.45, 36.4, 0.28, 0.85, 1.1, MAT.metal));
  brushes.push(box('cabinet_lockers_w_n', 16.02, 1.45, 39.6, 0.28, 0.85, 1.1, MAT.metal));

  addRoomShell(
    brushes,
    rooms,
    {
      id: 'lockern',
      x0: 15.75,
      z0: 44.5,
      x1: 21,
      z1: 51,
      floorY: Y0,
      height: H_ANNEX,
      gaps: [
        { side: 'w', along: 48 },
        { side: 'e', along: 48 },
      ],
      sparse: true,
    },
    { skipWall: { n: true, s: true, w: true, e: true }, ceiling: false },
  );
  brushes.push(box('wall_lockern_n', 18.5, H_ANNEX * 0.5, 51, 5.0, H_ANNEX, WALL_T, MAT.wall));
  brushes.push(box('cabinet_lockern_a', 17.2, 1.05, 49.2, 0.85, 2.1, 0.42, MAT.metal));
  brushes.push(box('cabinet_lockern_b', 19.4, 1.05, 49.2, 0.85, 2.1, 0.42, MAT.metal));
  brushes.push(box('cabinet_lockern_w_s', 16.02, 1.45, 46.2, 0.28, 0.85, 1.1, MAT.metal));
  brushes.push(box('cabinet_lockern_w_n', 16.02, 1.45, 49.42, 0.28, 0.85, 1.1, MAT.metal));

  // ── Labs (upper) ──────────────────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'lab',
      x0: 16,
      z0: 35,
      x1: LAB_X1,
      z1: 56,
      floorY: YU,
      height: WALL_H,
      gaps: [
        { side: 'w', along: 54.3, width: 2.4 },
        { side: 'w', along: 43, width: 2.7 },
      ],
    },
    { skipWall: { w: true }, ceiling: true },
  );
  addWallWithDoorZ(brushes, 'wall_lab_w_s', 16, 35, 44.55, 43, YU, WALL_H, 2.7, WALL_H - 0.22);
  addWallWithDoorZ(brushes, 'wall_lab_w_n', 16, 44.55, 56, 54.3, YU, WALL_H, 2.4);
  const labFloor = brushes.find((b) => b.id === 'floor_lab');
  if (labFloor) {
    labFloor.invisible = true;
    labFloor.noCollision = true;
  }
  addFloorSlab(brushes, 'floor_lab_main', 21.15, 35, LAB_X1, 56, YU);
  addFloorSlab(brushes, 'floor_lab_s', 16, 35, 21.15, 41.5, YU);
  addFloorSlab(brushes, 'floor_lab_n', 16, 44.5, 21.15, 56, YU);
  addGuardRail(brushes, 'labhole_s', 16.0, 41.5, 21.1, 41.5, YU);
  addGuardRail(brushes, 'labhole_n', 16.0, 44.5, 21.1, 44.5, YU);

  // Glass bays sit east of the stair top (x≈21) so the landing/corridor is walkable.
  const labBayCy = wallCy(YU, WALL_H);
  brushes.push(box('wall_lab_bay_ab', LAB_BAY_CX, labBayCy, LAB_BAY_Z[1], LAB_BAY_SX, WALL_H, WALL_T, MAT.wall));
  brushes.push(box('wall_lab_bay_bc', LAB_BAY_CX, labBayCy, LAB_BAY_Z[2], LAB_BAY_SX, WALL_H, WALL_T, MAT.wall));
  addWallWithDoorZ(brushes, 'wall_lab_bay_a', LAB_GLASS_X, LAB_BAY_Z[0], 37.0, LAB_BAY_DOOR_Z[0], YU, WALL_H);
  addWallWithDoorZ(brushes, 'wall_lab_bay_b', LAB_GLASS_X, 46.8, LAB_BAY_Z[2], LAB_BAY_DOOR_Z[1], YU, WALL_H);
  addWallWithDoorZ(brushes, 'wall_lab_bay_c', LAB_GLASS_X, LAB_BAY_Z[2], 51.2, LAB_BAY_DOOR_Z[2], YU, WALL_H);
  doors.push(doorOnZWall('lab_a', LAB_GLASS_X, LAB_BAY_DOOR_Z[0], -1, YU));
  doors.push(doorOnZWall('lab_b', LAB_GLASS_X, LAB_BAY_DOOR_Z[1], -1, YU));
  doors.push(doorOnZWall('lab_c', LAB_GLASS_X, LAB_BAY_DOOR_Z[2], -1, YU));
  addGlassWindows(brushes, group, {
    id: 'win_lab_a',
    axis: 'x',
    wall: LAB_GLASS_X,
    along0: 37.0,
    along1: LAB_BAY_Z[1],
    floorY: YU,
    height: WALL_H,
    openings: [{ center: 39.5, width: 3.4 }],
  });
  addGlassWindows(brushes, group, {
    id: 'win_lab_b',
    axis: 'x',
    wall: LAB_GLASS_X,
    along0: LAB_BAY_Z[1],
    along1: 46.8,
    floorY: YU,
    height: WALL_H,
    openings: [{ center: 44.3, width: 3.4 }],
  });
  addGlassWindows(brushes, group, {
    id: 'win_lab_c',
    axis: 'x',
    wall: LAB_GLASS_X,
    along0: 51.2,
    along1: LAB_BAY_Z[3],
    floorY: YU,
    height: WALL_H,
    openings: [{ center: 53.6, width: 3.4 }],
  });

  brushes.push(box('desk_lab_a', 26.5, YU + 0.55, 38.5, 2.2, 0.85, 0.95, MAT.desk));
  brushes.push(box('desk_lab_b', 28.5, YU + 0.55, 45.5, 2.2, 0.85, 0.95, MAT.desk));
  brushes.push(box('desk_lab_c', 28.5, YU + 0.55, 52.8, 2.2, 0.85, 0.95, MAT.desk));
  brushes.push(box('crate_lab1', 18.2, YU + 0.4, 38.2, 1.15, 0.8, 0.95, MAT.accent));
  brushes.push(box('sandbag_lab', 30.2, YU + 0.35, 53.5, 1.6, 0.55, 0.7, MAT.accent));

  // ── Bottling (double-height) ──────────────────────────────────
  addRoomShell(
    brushes,
    rooms,
    {
      id: 'bottle',
      x0: -12,
      z0: 51,
      x1: 12,
      z1: 74,
      floorY: Y0,
      height: H_BOTTLE,
      gaps: [
        { side: 's', along: 0 },
        { side: 'e', along: 54.3, width: 2.4 },
      ],
    },
    { skipWall: { s: true, e: true } },
  );
  // Tall 7.2 m shell walls share volume at the corners and under the catwalk
  // opening — inset so those faces no longer occupy the same plane.
  const wallBn = brushes.find((b) => b.id === 'wall_bottle_n');
  if (wallBn) wallBn.size.x = 24 - WALL_T;
  const wallBw = brushes.find((b) => b.id === 'wall_bottle_w');
  if (wallBw) wallBw.size.z = 23 - WALL_T;
  plugWidthGap(brushes, 'wall_bottle_s_plug', 51, -10, 10, -12, 12, Y0, H_BOTTLE);
  brushes.push(box('wall_bottle_s_upper', 0, 5.125, 51, 20, 4.15, WALL_T, MAT.wall));
  const bottleDoorZ = 54.3;
  const bottleDoorW = 2.4;
  const bottleDoorA = bottleDoorZ - bottleDoorW * 0.5;
  const bottleDoorB = bottleDoorZ + bottleDoorW * 0.5;
  const eSouth0 = 51 + WALL_T * 0.5;
  const eNorth1 = 74 - WALL_T * 0.5;
  const lintelY0 = 5.9;
  const lintelH = H_BOTTLE - lintelY0;
  const pierH = lintelY0;
  const pierCy = pierH * 0.5;
  brushes.push(
    box('wall_bottle_e_s', 12, pierCy, (eSouth0 + bottleDoorA) * 0.5, WALL_T, pierH, bottleDoorA - eSouth0, MAT.wall),
  );
  brushes.push(
    box('wall_bottle_e_n', 12, pierCy, (bottleDoorB + eNorth1) * 0.5, WALL_T, pierH, eNorth1 - bottleDoorB, MAT.wall),
  );
  brushes.push(box('wall_bottle_e_low', 12, 1.78, bottleDoorZ, WALL_T, 3.55, bottleDoorW, MAT.wall));
  // One header across the whole east wall so the door has no vertical lintel seam.
  brushes.push(
    box(
      'wall_bottle_e_hi',
      12,
      lintelY0 + lintelH * 0.5,
      (eSouth0 + eNorth1) * 0.5,
      WALL_T,
      lintelH,
      eNorth1 - eSouth0,
      MAT.wall,
    ),
  );

  brushes.push(box('pillar_bot_a', -5, 1.5, 62, 2.2, 3.0, 2.2, MAT.metal));
  brushes.push(box('pillar_bot_b', 5, 1.5, 62, 2.2, 3.0, 2.2, MAT.metal));
  brushes.push(box('pillar_bot_c', 0, 1.5, 69.5, 2.0, 3.0, 2.0, MAT.metal));
  brushes.push(box('crate_bot1', -9.2, 0.5, 55.5, 1.5, 1.0, 1.2, MAT.accent));
  brushes.push(box('crate_bot2', 8.2, 0.5, 71.5, 1.5, 1.0, 1.2, MAT.accent));
  brushes.push(box('sandbag_bot', -3.2, 0.35, 72.2, 2.2, 0.55, 0.75, MAT.accent));

  // Catwalk: lab → bottling east wall → stair down
  addFloorSlab(brushes, 'catwalk_bridge', 12.0, 53.15, 16.05, 55.45, YU);
  addGuardRail(brushes, 'bridge_s', 12.0, 53.1, 16.0, 53.1, YU);
  addGuardRail(brushes, 'bridge_n', 12.0, 55.5, 16.0, 55.5, YU);
  addFloorSlab(brushes, 'catwalk_east', 9.58, 53.15, 11.68, 65.6, YU);
  addGuardRail(brushes, 'cw_w', 9.65, 53.3, 9.65, 65.45, YU);
  addGuardRail(brushes, 'cw_e', 11.6, 55.52, 11.6, 68.28, YU);
  addFloorSlab(brushes, 'lip_cw_stair', 9.58, 65.4, 11.68, 68.3, YU);
  // Meet the stair newels (south z≈66.11, north z≈68.19) so the runs join.
  addGuardRail(brushes, 'cw_lip_w', 9.65, 65.45, 9.72, 66.11, YU);
  addGuardRail(brushes, 'cw_lip_n', 9.72, 68.19, 11.6, 68.28, YU);
  addStairFlight(brushes, group, mats, 'botdn', 9.72, 67.15, 0, 9.1, 3.6, 2.2, YU);

  // Hole in default bottle east was skip; west wall intact.

  // Lights — fixtures on real ceilings only; never in stair/hole volumes.
  const lampY0 = Y0 + WALL_H - 0.2;
  const lampYb = YB + WALL_H - 0.2;
  const lampYu = YU + WALL_H - 0.2;
  const lampYsu = Y0 + H_STAIRUP - 0.2;
  const lampSpots: Array<[number, number, number, number]> = [
    [0, BATH_LAMP_Y, 3.2, 2.2],
    [0, BATH_LAMP_Y, 6.0, 2.2],
    [0, lampY0, 13.4, 2.2],
    [0, lampY0, 16, 2.4],
    [0, lampY0, 18.8, 2.2],
    [-11, YB + H_SHAFT_DN - 0.08, 16, 2.4],
    [-11.2, lampYb, 6.8, 2.0],
    [-19.0, lampYb, 12.2, 2.2],
    [-17, lampYb, 26, 2.4],
    [-14, lampYb, 36, 2.4],
    [-4, lampY0, 25, 2.4],
    [4, lampY0, 31, 2.4],
    [14.5, lampY0, 28, 2.4],
    [26.0, lampY0, 28, 2.6],
    [26.5, Y0 + H_ANNEX - 0.2, 40, 2.6],
    [26.5, Y0 + H_ANNEX - 0.2, 48, 2.4],
    [18.2, Y0 + H_ANNEX - 0.2, 38.2, 1.8],
    [18.2, Y0 + H_ANNEX - 0.2, 47.8, 1.8],
    [-4, lampY0, 40, 2.6],
    [4, lampY0, 47, 2.6],
    [13, lampYsu, 37.5, 2.0],
    [13, lampYsu, 48.5, 2.0],
    [18.2, lampYu, 38.0, 2.0],
    [23.0, lampYu, 43.0, 2.0],
    [18.2, lampYu, 50.5, 2.0],
    [28.5, lampYu, 38.5, 2.2],
    [28.5, lampYu, 45.5, 2.2],
    [28.5, lampYu, 52.5, 2.2],
    [-5, 6.9, 58, 3.0],
    [5, 6.9, 64, 3.0],
    [0, 6.9, 70, 3.2],
    [0, 4.4, 62, 2.8],
    [10.6, lampYu, 58, 2.0],
  ];
  for (const [x, y, z, len] of lampSpots) {
    const { mesh, bake } = createFluorescent(x, y, z, len);
    group.add(mesh);
    bakeLights.push(bake);
  }
  const beacons: Array<[number, number, number]> = [
    [2.4, lampY0, 16.2],
    [-3.6, lampY0, 27.4],
    [3.2, lampY0, 42.6],
    [24.2, lampY0, 26.4],
    [-15.4, lampYb, 30.5],
    [26.8, lampYu, 44.2],
    [24.8, Y0 + H_ANNEX - 0.18, 43.5],
    [0.0, 6.85, 63.2],
    [-10.4, lampYb, 7.2],
  ];
  for (const [x, y, z] of beacons) group.add(createAlarmBeacon(x, y, z));
  const ventFill = new THREE.Color(0xd0d6d2);
  for (const [x, z] of [
    [0, 6.2],
    [2.1, 4.76],
    [-2.1, 4.76],
    [3.75, 3.1],
    [-3.75, 2.0],
  ] as const) {
    bakeLights.push({
      position: new THREE.Vector3(x, VENT_FLOOR + 0.62, z),
      color: ventFill,
      intensity: 0.52,
      range: 3.3,
      roomId: 'vent',
    });
  }
  const warmFill = new THREE.Color(0xd8d2c4);
  const fill = (x: number, y: number, z: number, roomId: string, intensity: number, range: number) => {
    bakeLights.push({
      position: new THREE.Vector3(x, y, z),
      color: warmFill,
      intensity,
      range,
      roomId,
    });
  };
  fill(-13.5, YB + 2.4, 16, 'stairdn', 0.42, 5.5);
  fill(-8.2, 1.4, 16, 'stairdn', 0.38, 4.5);
  fill(-8.5, YB + 1.6, 6.5, 'stash_s', 0.4, 4.2);
  fill(-19.2, YB + 1.6, 7.5, 'stash_w', 0.38, 4.0);
  fill(-19.2, YB + 1.6, 17.5, 'stash_w', 0.38, 4.0);
  fill(26.5, 1.5, 44, 'gen', 0.36, 5.0);

  for (const brush of brushes) {
    if (
      brush.id.startsWith('crate_') ||
      brush.id.startsWith('sandbag_') ||
      brush.id === 'wall_lab_bay_ab' ||
      brush.id === 'wall_lab_bay_bc'
    ) {
      brush.explodable = true;
    }
  }

  for (const brush of brushes) {
    if (brush.invisible) continue;
    if (skipBrushMesh(brush.id)) continue;
    group.add(createBrushMesh(brush, mats));
  }

  bakeLights.push(...dressFacility(group, mats, rooms));

  const spawn = new THREE.Vector3(0, VENT_FLOOR + 0.08, 6.55);
  const spawnYaw = 0;

  const enemySpawns: EnemySpawn[] = [
    {
      kind: 'melee',
      position: new THREE.Vector3(-3.75, 0.05, 1.58),
      yaw: Math.PI,
      script: 'toiletSit',
      toiletDoorId: 'stall_0',
    },
    { kind: 'pistol', position: new THREE.Vector3(3.2, 0.05, 30.0), yaw: Math.PI },
    { kind: 'pistol', position: new THREE.Vector3(13.5, 0.05, 26.5), yaw: Math.PI },
    { kind: 'pistol', position: new THREE.Vector3(17.2, 0.05, 32.0), yaw: Math.PI * 0.5 },
    { kind: 'shotgun', position: new THREE.Vector3(-18.5, YB + 0.05, 30.0), yaw: 0 },
    { kind: 'pistol', position: new THREE.Vector3(-12.2, YB + 0.05, 38.5), yaw: Math.PI * 0.5 },
    { kind: 'pistol', position: new THREE.Vector3(-8.5, YB + 0.05, 4.2), yaw: 0 },
    { kind: 'shotgun', position: new THREE.Vector3(-20.2, YB + 0.05, 18.0), yaw: Math.PI * 0.5 },
    { kind: 'mp5', position: new THREE.Vector3(0.0, 0.05, 44.0), yaw: Math.PI },
    { kind: 'ak47', position: new THREE.Vector3(26.8, YU + 0.05, 45.2), yaw: Math.PI },
    { kind: 'pistol', position: new THREE.Vector3(28.5, YU + 0.05, 54.2), yaw: Math.PI },
    { kind: 'sawedoff', position: new THREE.Vector3(3.5, 0.05, 64.5), yaw: Math.PI },
    { kind: 'scientist', position: new THREE.Vector3(15.8, 0.05, 23.8), yaw: Math.PI },
    { kind: 'civilian', position: new THREE.Vector3(11.4, 0.05, 31.2), yaw: -Math.PI * 0.4 },
    { kind: 'scientist', position: new THREE.Vector3(26.4, 0.05, 27.6), yaw: Math.PI },
    { kind: 'pistol', position: new THREE.Vector3(27.2, 0.05, 43.4), yaw: Math.PI },
    { kind: 'scientist', position: new THREE.Vector3(29.2, YU + 0.05, 45.8), yaw: Math.PI },
    { kind: 'civilian', position: new THREE.Vector3(19.6, YU + 0.05, 54.6), yaw: -Math.PI * 0.5 },
  ];

  // Floor walkways only — keep off desks/crates/pillars/catwalk rails and
  // out of the lab stair hole (x 16–21.15, z 41.5–44.5).
  const pickups: PickupSpawnDef[] = [
    { kind: 'pistol_clip', x: 1.25, y: 0.14, z: 2.55 },
    { kind: 'weapon_shotgun', x: -1.2, y: 0.16, z: 24.0 },
    { kind: 'shells', x: 1.8, y: 0.14, z: 32.0 },
    { kind: 'medi_small', x: 16.5, y: 0.14, z: 30.2 },
    { kind: 'grenades', x: 30.2, y: 0.14, z: 49.0 },
    { kind: 'weapon_shotgun', x: 24.0, y: 0.16, z: 38.6 },
    { kind: 'pistol_clip', x: 12.4, y: 0.14, z: 29.0 },
    { kind: 'medi_small', x: -14.8, y: YB + 0.14, z: 31.0 },
    { kind: 'armor', x: -14.4, y: YB + 0.16, z: 4.6 },
    { kind: 'shells', x: -7.6, y: YB + 0.14, z: 4.4 },
    { kind: 'grenades', x: -20.6, y: YB + 0.14, z: 19.4 },
    { kind: 'mp5_clip', x: -20.4, y: YB + 0.14, z: 5.8 },
    { kind: 'weapon_mp5', x: 0.0, y: 0.16, z: 38.8 },
    { kind: 'mp5_clip', x: -2.5, y: 0.14, z: 47.5 },
    { kind: 'armor', x: 3.5, y: 0.16, z: 47.8 },
    { kind: 'pistol_clip', x: 28.0, y: YU + 0.14, z: 38.8 },
    { kind: 'shells', x: 20.0, y: YU + 0.14, z: 52.5 },
    { kind: 'mp5_clip', x: 30.0, y: YU + 0.14, z: 50.8 },
    { kind: 'weapon_ak47', x: 14.0, y: YU + 0.12, z: 54.3 },
    { kind: 'ak_clip', x: 10.6, y: YU + 0.12, z: 58.0 },
    { kind: 'weapon_sawedoff', x: 0.0, y: 0.16, z: 55.8 },
    { kind: 'medi_large', x: -6.5, y: 0.14, z: 57.5 },
    { kind: 'armor', x: 2.2, y: 0.16, z: 66.8 },
    { kind: 'superhealth', x: 4.5, y: 0.16, z: 72.5 },
    { kind: 'weapon_gl', x: -4.2, y: 0.16, z: 50.5 },
    { kind: 'grenades', x: -6.0, y: 0.14, z: 51.2 },
    { kind: 'weapon_rpg', x: 8.5, y: 0.16, z: 68.2 },
    { kind: 'rockets', x: 10.2, y: 0.14, z: 67.4 },
  ];

  // Occupancy only — do not rebuild walls. Stair flights sit past x=16;
  // the lab/bottle catwalk sits in the 12–16 gap.
  const stairup = rooms.find((r) => r.id === 'stairup');
  if (stairup) stairup.walk = { x1: 21.2 };
  const bottle = rooms.find((r) => r.id === 'bottle');
  if (bottle) bottle.x1 = 16.05;

  return {
    group,
    brushes,
    lights,
    spawn,
    spawnYaw,
    spawnCrouch: true,
    doors,
    mats,
    enemySpawns,
    pickups,
    rooms,
    bakeLights,
  };
}
